import React, { useState } from "react";
import { FormInput, FormTextarea, FormButton } from "./FormComponents";

export interface JournalFormData {
  title: string;
  content: string;
  date: string;
  tags: string;
  mood: string;
}

interface JournalFormProps {
  initialData: JournalFormData;
  onSubmit: (data: JournalFormData) => Promise<void>;
  onCancel: () => void;
  submitButtonText: string;
  loading: boolean;
  title: string;
}

const MOOD_OPTIONS = [
  { value: "happy", label: "Happy" },
  { value: "sad", label: "Sad" },
  { value: "neutral", label: "Neutral" },
  { value: "excited", label: "Excited" },
  { value: "angry", label: "Angry" },
  { value: "calm", label: "Calm" },
  { value: "anxious", label: "Anxious" },
  { value: "content", label: "Content" },
];

const JournalForm: React.FC<JournalFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  submitButtonText,
  loading,
  title,
}) => {
  const [formData, setFormData] = useState<JournalFormData>(initialData);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.content.trim() || !formData.mood) {
      return;
    }

    await onSubmit(formData);
  };

  return (
    <div className="bg-white/[0.05] dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.08] rounded-2xl p-5 lg:p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormInput
          id="title"
          label="Journal Title"
          value={formData.title}
          onChange={(value) => setFormData({ ...formData, title: value })}
          placeholder="What's on your mind today?"
          required
        />

        <FormTextarea
          id="content"
          label="Journal Content"
          value={formData.content}
          onChange={(value) => setFormData({ ...formData, content: value })}
          placeholder="Write your thoughts, feelings, and reflections here..."
          rows={8}
          required
        />

        <FormInput
          id="date"
          label="Date"
          type="date"
          value={formData.date}
          onChange={(value) => setFormData({ ...formData, date: value })}
          required
        />

        <div>
          <label className="block text-[18px] font-medium text-neutral-700 dark:text-white/60 mb-2">
            Mood
          </label>
          <select
            value={formData.mood}
            onChange={(e) => setFormData({ ...formData, mood: e.target.value })}
            className="w-full px-4 py-3.5 rounded-xl border border-black/[0.1] bg-black/[0.04] text-neutral-900 focus:outline-none focus:border-[#f5a623] transition-colors duration-150 dark:border-white/[0.1] dark:bg-white/[0.06] dark:text-neutral-100"
            required
          >
            {MOOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <FormInput
          id="tags"
          label="Tags"
          value={formData.tags}
          onChange={(value) => setFormData({ ...formData, tags: value })}
          placeholder="Enter tags separated by commas (e.g., work, personal, reflection)"
        />

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6">
          <div className="">
            <FormButton
              type="submit"
              variant="primary"
              loading={loading}
              disabled={
                !formData.title.trim() ||
                !formData.content.trim() ||
                !formData.mood
              }
              size="lg"
            >
              {submitButtonText}
            </FormButton>
          </div>
          <div className="">
            <FormButton
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={loading}
              size="lg"
            >
              Cancel
            </FormButton>
          </div>
        </div>
      </form>
    </div>
  );
};

export default JournalForm;
